<template>
  <div class="settings-grid">
    <mobile-menu-group
      v-for="(group, index) in groups"
      :id="group.TabIdName"
      :key="index"
      :header-text="group.headerText"
      :items="group.items"
      @feedback="increaseFeedbackModalKey()"/>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import MobileMenuGroup from '@/views/modules/_components/MobileMenuGroup.vue';

/**
 * User settings menu with flattened navigation
 *
 * @module views/modules/user-account/UserSettingsMenu
 * @author Dilan Useche <dilan8810@gmail.com>
 *
 * @vue-data {Object[]} [groups=[...]] - groups to show in the menu
 */
export default {
  name: 'UserSettingsMenu',
  i18n: {
    messages: {
      en: {
        AccountDetailsText: 'Manage your profile information',
        BrandingText: 'Customize your brand appearance',
        CompanyPoliciesText: 'Configure your business policies',
        OpeningHoursText: 'Set your business hours',
        SecurityText: 'Manage your password and security',
        PrepaidPackagesTitle: 'Prepaid packages',
        PrepaidPackagesText: 'Manage your prepaid packages',
        DedicatedNumbersTitle: 'Dedicated numbers',
        DedicatedNumbersText: 'Manage your dedicated numbers',
        AutoTopUpText: 'Set up automatic top-ups',
        PaymentMethodsText: 'Manage your payment methods',
        BillingHistoryText: 'View your billing history',
      },
    },
  },
  components: {
    MobileMenuGroup,
  },
  data() {
    return {
    };
  },
  computed: {
    groups() {
      return [
        {
          headerText: this.$tc('$General.Account'),
          TabIdName: 'settings-group-account',
          items: [
            {
              title: this.$t('$UserSettings.$Account.$AccountDetails.TabTitle'),
              text: this.$t('AccountDetailsText'),
              icon: 'UserIcon',
              path: '/settings/account-details',
            },
            {
              title: this.$t('$UserSettings.$Account.$Branding.TabTitle'),
              text: this.$t('BrandingText'),
              icon: 'ImageIcon',
              path: '/settings/branding',
            },
            {
              title: this.$t('$UserSettings.$Account.$CompanyPolicies.TabTitle'),
              text: this.$t('CompanyPoliciesText'),
              icon: 'FileTextIcon',
              path: '/settings/company-policies',
            },
            {
              title: this.$t('$UserSettings.$Account.$OpeningHours.TabTitle'),
              text: this.$t('OpeningHoursText'),
              icon: 'ClockIcon',
              path: '/settings/opening-hours',
            },
            {
              title: this.$tc('$General.Security'),
              text: this.$t('SecurityText'),
              icon: 'ShieldIcon',
              path: '/settings/security',
            },
            {
              title: this.$tc('$General.Integration', 2),
              text: 'Connect external apps',
              icon: 'GridIcon',
              path: '#',
              isDisabled: true,
              badge: this.$t('$General.ComingSoon'),
              badgeColor: 'light',
            },
          ],
        },
        {
          headerText: this.$tc('$General.Shop', 1),
          TabIdName: 'settings-group-shop',
          items: [
            {
              title: this.$t('PrepaidPackagesTitle'),
              text: this.$t('PrepaidPackagesText'),
              icon: 'BoxIcon',
              path: '/settings/prepaid-packages',
            },
            {
              title: this.$t('DedicatedNumbersTitle'),
              text: this.$t('DedicatedNumbersText'),
              icon: 'PhoneIcon',
              path: '/settings/dedicated-numbers',
            },
            {
              title: this.$t('$UserSettings.$Shop.$ManageTopUps.$AutoTopUpAccount.TabTitle'),
              text: this.$t('AutoTopUpText'),
              icon: 'DollarSignIcon',
              path: '/settings/auto-top-up',
            },
          ],
        },
        {
          headerText: this.$tc('$General.Billing'),
          TabIdName: 'settings-group-billing',
          items: [
            {
              title: this.$t('$UserSettings.$Billing.$PaymentMethods.TabTitle'),
              text: this.$t('PaymentMethodsText'),
              icon: 'CreditCardIcon',
              path: '/settings/payment-methods',
            },
            {
              title: this.$t('$UserSettings.$Billing.$History.TabTitle'),
              text: this.$t('BillingHistoryText'),
              icon: 'FileIcon',
              path: '/settings/billing-history',
            },
          ],
        },
      ];
    },
  },
  methods: {
    ...mapMutations({
      increaseFeedbackModalKey: 'INCREASE_FEEDBACK_MODAL_KEY',
    }),
  },
};

</script>

<style lang="scss" scoped>
.settings-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;

  @media only screen and (min-width: 576px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media only screen and (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
}
</style>
